/**
 * Cookie Consent API Service
 *
 * Thin wrapper around the `/api/v1/consents` endpoints.
 *
 * Usable from BOTH the framework-agnostic consent core (called before
 * React boots, from Devise/login pages) and the React observer. Because
 * this service can run before the React app mounts, it does NOT depend on
 * the shared `apiClient` (which assumes a React-context auth token flow).
 * It uses native `fetch` and attaches the JWT from `localStorage` when
 * available so authenticated visitors still have their `user_id`
 * populated on the resulting row. When absent, the server-side controller
 * records the row against the `as_anonymous_id` cookie.
 *
 * Errors are swallowed and surfaced via the returned discriminated result
 * so the caller (the consent core) can fall back to cookie-only state if
 * the API call fails.
 */

const API_BASE = "/api/v1";

/**
 * Shape sent on POST/PATCH.
 */
export interface ConsentApiRequest {
  categories: { necessary: boolean; analytics: boolean; advertising: boolean };
  source: string;
  consent_version: string;
  gpc_detected: boolean;
}

/**
 * Shape returned by GET /current when a row exists.
 */
export interface ConsentApiCurrentResponse {
  id: string;
  categories: { necessary: boolean; analytics: boolean; advertising: boolean };
  consent_version: string;
  gpc_detected: boolean;
  source: string;
  created_at: string;
}

/**
 * Shape returned by POST/PATCH on success.
 */
export interface ConsentApiSaveResponse {
  id: string;
  categories: { necessary: boolean; analytics: boolean; advertising: boolean };
}

export type ConsentApiResult<T> =
  | { ok: true; data: T | null }
  | { ok: false; error: string };

/**
 * Fetch the most recent consent row for the current visitor.
 *
 * @returns `{ ok: true, data }` with the row, `{ ok: true, data: null }`
 *   on 204 No Content, or `{ ok: false, error }` on network/5xx.
 */
export async function getCurrentConsent(): Promise<
  ConsentApiResult<ConsentApiCurrentResponse>
> {
  return request<ConsentApiCurrentResponse>(
    "GET",
    `${API_BASE}/consents/current`,
  );
}

/**
 * Create a new ConsentLog row.
 *
 * @param payload - the decision to persist
 * @returns `{ ok: true, data }` with the created row id + categories,
 *   or `{ ok: false, error }` on network/5xx/validation failure.
 */
export async function postConsent(
  payload: ConsentApiRequest,
): Promise<ConsentApiResult<ConsentApiSaveResponse>> {
  return request<ConsentApiSaveResponse>(
    "POST",
    `${API_BASE}/consents`,
    payload,
  );
}

/**
 * PATCH alias of `postConsent`. The server is append-only and treats
 * PATCH and POST identically; no app surface calls it.
 *
 * @param payload - the decision to persist
 */
export async function patchConsent(
  payload: ConsentApiRequest,
): Promise<ConsentApiResult<ConsentApiSaveResponse>> {
  return request<ConsentApiSaveResponse>(
    "PATCH",
    `${API_BASE}/consents`,
    payload,
  );
}

/**
 * Perform an authenticated-when-possible fetch. JWT is attached when
 * present; otherwise the request is anonymous and the controller uses
 * the `as_anonymous_id` cookie instead.
 *
 * @param method - HTTP verb
 * @param url - full path including `/api/v1` prefix
 * @param body - optional JSON-serialisable payload
 * @returns discriminated `ConsentApiResult`
 */
async function request<T>(
  method: "GET" | "POST" | "PATCH",
  url: string,
  body?: unknown,
): Promise<ConsentApiResult<T>> {
  try {
    const headers: Record<string, string> = {
      "X-Requested-With": "XMLHttpRequest",
      Accept: "application/json",
    };
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const token = readAuthToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const csrf = readCsrfToken();
    if (csrf) headers["X-CSRF-Token"] = csrf;

    const response = await fetch(url, {
      method,
      headers,
      credentials: "same-origin",
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.status === 204) return { ok: true, data: null };

    if (!response.ok) {
      return {
        ok: false,
        error: `Request failed with status ${response.status}`,
      };
    }

    const data = (await response.json()) as T;
    return { ok: true, data };
  } catch (error) {
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Network error",
    };
  }
}

/**
 * Read the JWT from localStorage. Returns `null` on any error so this
 * works safely in environments where `localStorage` is unavailable (e.g.
 * some jsdom configurations).
 *
 * @returns the token string, or `null`
 */
function readAuthToken(): string | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage.getItem("auth_token");
  } catch {
    return null;
  }
}

/**
 * Read the Rails CSRF token from the `<meta name="csrf-token">` tag
 * rendered by `csrf_meta_tags`. The Devise session authenticator relies
 * on this header so a logged-in visitor on a Devise page sees their row
 * associated with their user id.
 *
 * @returns the CSRF token, or `null` when the meta tag is missing
 */
function readCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  const tag = document.querySelector('meta[name="csrf-token"]');
  return tag?.getAttribute("content") ?? null;
}
